import { useCallback, useEffect, useState } from 'react';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
} from '~/domain/events';
import { getContourUndoService } from '~/domain/ServiceContainer';
import {
    ContourEditStep,
    ContourHistoryEntry,
    ContourJump,
    ContourRestore,
    isSameContourStep,
} from '~/domain/undo';
import type { RoiId } from '~/domain/changes/ids';

/**
 * Announces a restored snapshot, passing the restore through so callers can
 * both act on it and return it. A missing result means the service declined
 * the move; nothing is published for it.
 */
const publishRestore = (
    result: ContourRestore | undefined,
    EventClass:
        | typeof ContourUndoRequestedEvent
        | typeof ContourRedoRequestedEvent
): ContourRestore | undefined => {
    if (!result) {
        return undefined;
    }
    const event = new EventClass(
        result.structureSetUid,
        result.roiId,
        result.compressedPixels,
        result.grid,
        'user',
        result.steps
    );
    domainEventBus.publish(event).catch((error) => {
        console.error('Failed to publish contour event:', error);
    });
    return result;
};

/**
 * How long {@link captureRecordedContourEdit} waits for the write's entry
 * before giving up. Generous: the wait is for one timer hop plus the
 * handler's awaits, and the cost of expiring early is an Undo that
 * declines an edit it could have reverted.
 */
const STEP_RECORDED_TIMEOUT_MS = 5000;

/**
 * The step a write records, resolved once it actually exists in history.
 *
 * The entry is NOT there when the write returns: the ROI publishes its
 * change on a timer and the handler awaits a snapshot before recording it.
 * Reading the history synchronously therefore names the PREVIOUS step, so
 * an offer built on it either declines an edit it should revert or, once
 * the user has undone their way back, reverts a different edit than the one
 * it is labelled with.
 *
 * Call BEFORE the write so the baseline is the step it supersedes.
 * Resolves undefined if nothing is recorded within the timeout — the offer
 * then declines, which is the safe direction.
 */
export const captureRecordedContourEdit = (
    roiId: RoiId
): Promise<ContourEditStep | undefined> => {
    const baseline = getContourUndoService().currentStep(roiId);
    return new Promise((resolve) => {
        // A holder, because `subscribe` invokes its listener before
        // returning: on that first call the handle it will return does not
        // exist yet, and the listener has to be able to reach it later.
        const pending: {
            subscription?: { unsubscribe: () => void };
            timer?: ReturnType<typeof setTimeout>;
            settled: boolean;
        } = { settled: false };

        const settle = (step: ContourEditStep | undefined) => {
            if (pending.settled) {
                return;
            }
            pending.settled = true;
            if (pending.timer !== undefined) {
                clearTimeout(pending.timer);
            }
            queueMicrotask(() => pending.subscription?.unsubscribe());
            resolve(step);
        };

        const check = () => {
            const current = getContourUndoService().currentStep(roiId);
            // An ROI edited for the first time this session records the
            // state BEFORE the edit as its own entry, and that lands first.
            // Settling on it would name a step the write is not, and the
            // offer would then decline the very deletion it is attached to.
            if (
                current !== 'initial' &&
                !isSameContourStep(current, baseline)
            ) {
                settle(current);
            }
        };

        pending.timer = setTimeout(
            () => settle(undefined),
            STEP_RECORDED_TIMEOUT_MS
        );
        // Never rejects: the caller starts this and does not await it, so a
        // throw here would surface as an unhandled rejection instead of the
        // declined offer that not knowing the step actually means.
        try {
            pending.subscription = getContourUndoService().subscribe(check);
            if (pending.settled) {
                pending.subscription.unsubscribe();
            }
        } catch (error) {
            console.error('Capturing the contour edit step failed:', error);
            settle(undefined);
        }
    });
};

/**
 * Undo `step` — and only `step`. The ROI is addressed explicitly rather
 * than through the current selection, and the step explicitly rather than
 * through "whatever is newest".
 *
 * For an affordance that outlives the edit it names: an Undo offered on a
 * notification stays on screen while the user carries on editing, and the
 * plain undo it would otherwise perform reverts whichever edit is newest by
 * then — a different edit from the one the control is labelled with.
 * Declines instead, so the caller can say the offer has lapsed.
 *
 * @returns whether the ROI was reverted.
 */
export const undoContourEditIfCurrent = (
    roiId: RoiId,
    step: ContourEditStep
): boolean => {
    const current = getContourUndoService().currentStep(roiId);
    if (!isSameContourStep(current, step)) {
        return false;
    }
    return (
        publishRestore(
            getContourUndoService().undo(roiId),
            ContourUndoRequestedEvent
        ) !== undefined
    );
};

/**
 * A jump can move the pointer either way, and the two restore events are
 * handled identically downstream — but listeners keyed on 'undo' vs 'redo'
 * (the difference overlay, the 3D preview) would read a backwards jump
 * announced as a redo as movement in the wrong direction.
 */
const publishJump = (
    jump: ContourJump | undefined
): ContourJump | undefined => {
    if (jump) {
        publishRestore(
            jump.result,
            jump.direction === 'backward'
                ? ContourUndoRequestedEvent
                : ContourRedoRequestedEvent
        );
    }
    return jump;
};

export interface UndoRedoState {
    canUndo: boolean;
    canRedo: boolean;
    /** True when the ROI has recorded edits that a revert would discard. */
    canRevertToInitial: boolean;
    /** Every retained step, oldest first. */
    history: ContourHistoryEntry[];
    /**
     * Intermediate steps dropped to keep the stack within its size cap. When
     * non-zero, {@link history} has a gap between the initial state and the
     * oldest retained edit.
     */
    discardedStepCount: number;
}

const EMPTY_STATE: UndoRedoState = {
    canUndo: false,
    canRedo: false,
    canRevertToInitial: false,
    history: [],
    discardedStepCount: 0,
};

/**
 * Undo/redo state and actions for one ROI. No ROI (`null`) yields empty
 * state and inert actions.
 */
export const useUndoRedo = (roiId: RoiId | null) => {
    const [state, setState] = useState<UndoRedoState>(EMPTY_STATE);

    useEffect(() => {
        if (roiId === null) {
            setState(EMPTY_STATE);
            return;
        }
        const contourUndoService = getContourUndoService();
        const updateState = () => {
            setState({
                canUndo: contourUndoService.canUndo(roiId),
                canRedo: contourUndoService.canRedo(roiId),
                canRevertToInitial:
                    contourUndoService.canRevertToInitial(roiId),
                history: contourUndoService.getHistory(roiId),
                discardedStepCount:
                    contourUndoService.getDiscardedStepCount(roiId),
            });
        };
        const subscription = contourUndoService.subscribe(updateState);
        return subscription.unsubscribe;
    }, [roiId]);

    const undo = () =>
        roiId === null
            ? undefined
            : publishRestore(
                  getContourUndoService().undo(roiId),
                  ContourUndoRequestedEvent
              );

    const redo = () =>
        roiId === null
            ? undefined
            : publishRestore(
                  getContourUndoService().redo(roiId),
                  ContourRedoRequestedEvent
              );

    /** Restore the ROI to a step from {@link UndoRedoState.history}. */
    const jumpTo = useCallback(
        (index: number) =>
            roiId === null
                ? undefined
                : publishJump(getContourUndoService().jumpTo(roiId, index)),
        [roiId]
    );

    /** Restore the ROI to its state before this session's first edit. */
    const revertToInitial = useCallback(
        () =>
            roiId === null
                ? undefined
                : publishJump(getContourUndoService().revertToInitial(roiId)),
        [roiId]
    );

    return { ...state, undo, redo, jumpTo, revertToInitial };
};
